import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useApiClient } from '@/api/clients/useApiClient.util.ts';
import { DictionaryItemDto, DictionaryTypeDto } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { RefetchModel } from '@/api/model/Refetch.model.ts';

export type DictionariesType = Partial<Record<DictionaryTypeDto, DictionaryItemDto[]>>;

interface Return {
  dictionaries?: DictionariesType;
  getAllDictionaries: RefetchModel<DictionariesType>;
  isFetchingDictionariesTypes: boolean;
}

/** Every dictionary entry grouped by type; `enabled` false skips the request (no dictionary read). */
export const useGetAllDictionaries = ({ enabled = true }: { enabled?: boolean } = {}): Return => {
  const { dictionariesApi } = useApiClient();

  const { data, isLoading, refetch } = useQuery<DictionariesType, AxiosError>({
    enabled,
    queryFn: async () => {
      const response = await dictionariesApi.getDictionaries(0, 10000, {});
      const dicts: DictionariesType = {};
      response.data.items.forEach(it => {
        (dicts[it.type] ??= []).push(it);
      });

      return dicts;
    },
    queryKey: [QueryKeyEnum.DICTIONARIES.ALL],
  });

  return {
    dictionaries: data,
    getAllDictionaries: refetch,
    isFetchingDictionariesTypes: isLoading,
  };
};
